import gql from "graphql-tag";

import type {PerseusRenderer} from "@khanacademy/perseus-core";
import {isTruthy} from "@khanacademy/wonder-stuff-core";
import {parsePerseusItem} from "@khan/component-perseus";
import {
    getAnswersFromWidgets,
    injectWidgets,
} from "@khan/component-perseus/perseus";
import type {ApolloGqlClient} from "@khan/data-gql-apollo";
import {gqlOp, type GqlOperation} from "@khan/data-gql-core";
import {sanitizeTex, setPromptVariable} from "@khan/guide-core-helpers";

import {CONTENT_GET_PREREQUISITE_EXERCISES} from "#content-library/ui/content-renderers/exercise/prerequisite-exercises-query.ts";

import type {
    AITeacherTools_ExerciseDetails_exerciseById_assessmentItems as ExerciseAssessmentItems,
    AITeacherTools_ExerciseDetails as ExerciseDetails,
    AITeacherTools_ExerciseDetailsVariables as ExerciseDetailsVariables,
} from "./__graphql-types__/AITeacherTools_ExerciseDetails.ts";
import {setSharedDetails, unsetSharedDetails} from "./shared-details.ts";

export const EXERCISE_DETAILS_QUERY: GqlOperation<
    ExerciseDetails,
    ExerciseDetailsVariables
> = gqlOp(gql`
    query AITeacherTools_ExerciseDetails($exerciseId: String!) {
        exerciseById(id: $exerciseId) {
            id
            translatedTitle
            prerequisites
            defaultUrlPath
            parentTopic {
                id
                domainSlug
                allLearnableContent(includeUnlisted: false) {
                    id
                    translatedTitle
                    defaultUrlPath
                }
            }
            assessmentItems {
                id
                itemShapeType
                itemData
                problemType
            }
        }
    }
`);

export async function loadExerciseDetails(
    gqlClient: ApolloGqlClient,
    exerciseId: string,
) {
    const response = await gqlClient.query(EXERCISE_DETAILS_QUERY, {
        variables: {exerciseId},
        fetchPolicy: "cache-first",
    });

    const exercise = response.data?.exerciseById;
    if (!exercise) {
        return;
    }

    const domain = exercise.parentTopic?.domainSlug ?? "";
    if (domain) {
        setPromptVariable("domain", domain);
    }

    setPromptVariable("contentTitle", exercise.translatedTitle);

    const {
        firstQuestionContent,
        firstQuestionAnswer,
        questionBankFull,
        questionBankOnePerType,
        answerBankFull,
        answerBankOnePerType,
        hintsBankFull,
        hintsBankOnePerType,
    } = collectAssessmentItems(exercise.assessmentItems);

    // For historical reasons, we set the vaguely named 'context' variable,
    // but we also set more descriptively-named variables:
    setPromptVariable("context", firstQuestionContent);
    setPromptVariable("correctanswer", firstQuestionAnswer);

    setPromptVariable("questionBankFull", joinSections(questionBankFull));
    setPromptVariable(
        "questionBankOnePerType",
        joinSections(questionBankOnePerType),
    );
    setPromptVariable("questionBankFirstItem", questionBankOnePerType[0]);

    setPromptVariable("answerBankFull", joinSections(answerBankFull));
    setPromptVariable(
        "answerBankOnePerType",
        joinSections(answerBankOnePerType),
    );

    setPromptVariable("hintsBankFull", joinSections(hintsBankFull));
    setPromptVariable("hintsBankOnePerType", joinSections(hintsBankOnePerType));

    setSharedDetails(exercise);

    const prerequisiteSlugs = exercise.prerequisites;
    if (!prerequisiteSlugs || !prerequisiteSlugs.length) {
        return;
    }

    setPromptVariable("prerequisiteSlugs", prerequisiteSlugs.join(", "));

    gqlClient
        .query(CONTENT_GET_PREREQUISITE_EXERCISES, {
            variables: {slugs: prerequisiteSlugs},
            fetchPolicy: "cache-first",
        })
        .then((prereqResponse) => {
            const prereqExercises = prereqResponse.data?.exercisesBySlugs;
            if (!prereqExercises) {
                return;
            }

            const prerequisiteDetails = prereqExercises
                .filter(isTruthy)
                .map((ex) => {
                    const standards = (ex.mappedStandards || [])
                        .map((s) => s?.standardId)
                        .join(", ");
                    return `- [${ex.translatedTitle}](/e/${ex.slug}): ${ex.translatedDescription} (standards: ${standards})`;
                });

            setPromptVariable(
                "prerequisiteDetails",
                prerequisiteDetails.join("\n"),
            );
        });
}

export function unloadExerciseDetails() {
    [
        "contentTitle",
        "context",
        "correctanswer",
        "domain",
        "questionBankFull",
        "questionBankOnePerType",
        "questionBankFirstItem",
        "answerBankFull",
        "answerBankOnePerType",
        "hintsBankFull",
        "hintsBankOnePerType",
        "prerequisiteSlugs",
        "prerequisiteDetails",
    ].forEach((variable) => {
        setPromptVariable(variable, null);
    });
    unsetSharedDetails();
}

const collectAssessmentItems = (
    assessmentItems: Nullable<ExerciseAssessmentItems>[],
) => {
    const questionBankFull: string[] = [];
    const questionBankOnePerType: string[] = [];
    const answerBankFull: string[] = [];
    const answerBankOnePerType: string[] = [];
    const hintsBankFull: string[] = [];
    const hintsBankOnePerType: string[] = [];

    const seenGroups = new Set<string>();

    let firstQuestionContent;
    let firstQuestionAnswer;

    for (const item of assessmentItems) {
        if (!item?.itemData || item.itemShapeType !== "item") {
            continue;
        }

        const itemData = parsePerseusItem(item.itemData);
        const {question, hints} = itemData;

        const content = injectWidgets(question.content, question.widgets);
        const answers = getAnswersFromWidgets(question.widgets).join(", ");

        if (!firstQuestionContent) {
            firstQuestionContent = sanitizeTex(content);
        }
        if (!firstQuestionAnswer) {
            firstQuestionAnswer = sanitizeTex(answers);
        }

        const questionNumber = questionBankFull.length + 1;
        questionBankFull.push(
            numberedQuestionAnswer(questionNumber, content, answers),
        );
        answerBankFull.push(numberedAnswer(questionNumber, answers));
        hintsBankFull.push(numberedHints(questionNumber, hints));

        if (item.problemType && seenGroups.has(item.problemType)) {
            continue;
        }

        const currentNumber = questionBankOnePerType.length + 1;
        questionBankOnePerType.push(
            numberedQuestionAnswer(currentNumber, content, answers),
        );
        answerBankOnePerType.push(numberedAnswer(currentNumber, answers));
        hintsBankOnePerType.push(numberedHints(currentNumber, hints));

        seenGroups.add(item.problemType || "");
    }

    return {
        firstQuestionContent,
        firstQuestionAnswer,
        questionBankFull,
        questionBankOnePerType,
        answerBankFull,
        answerBankOnePerType,
        hintsBankFull,
        hintsBankOnePerType,
    };
};

const numberedQuestionAnswer = (
    number: number,
    question: string,
    answer: string,
) =>
    `Question ${number}: ${sanitizeTex(question)}\nAnswer: ${sanitizeTex(
        answer,
    )}`;

const numberedAnswer = (number: number, answer: string) =>
    `Answer ${number}: ${sanitizeTex(answer)}`;

const numberedHints = (number: number, hints: ReadonlyArray<PerseusRenderer>) =>
    `Hints for ${number}:\n${hints
        .map(
            (hint) =>
                "- " + sanitizeTex(injectWidgets(hint.content, hint.widgets)),
        )
        .join("\n")}`;

const joinSections = (sections: string[]) => sections.join("\n---\n").trim();
